import { ActionTree, Module, MutationTree } from 'vuex';
import { RootStateInterface } from '@/store/rootState';
import { ClientsListStateInterface } from '../interfaces/ClientsListState';
import { Client } from '@/modules/clients/models/Client';
import { ClientsSourceResponse } from '../models/ClientsSourceResponse';
import { ClientsSourceRequest } from '../models/ClientsSourceRequest';
import { ClientsListApi } from '@/modules/clients/api/clientsList';

export const state: ClientsListStateInterface = {
    clients: [],
    clientsByMortgageCache: {},
}

export const mutations: MutationTree<ClientsListStateInterface> = {
    setClients(oldState: ClientsListStateInterface, payload: Client[]) {
        oldState.clients = payload;
    },
    setClientsByMortgageCache(
      oldState: ClientsListStateInterface,
      payload: { requestKey: string; data: ClientsSourceResponse },
    ) {
      oldState.clientsByMortgageCache = {
        ...oldState.clientsByMortgageCache,
        [payload.requestKey]: payload.data,
      };
    },
    invalidateClientsByMortgageCache(oldState: ClientsListStateInterface, requestKey?: string) {
      if (!requestKey) {
        oldState.clientsByMortgageCache = {};
        return;
      }
      const next = { ...oldState.clientsByMortgageCache };
      delete next[requestKey];
      oldState.clientsByMortgageCache = next;
    },
} 

const clientsByMortgageInFlight: Record<
  string,
  Promise<ClientsSourceResponse> | undefined
> = {};

export const actions: ActionTree<ClientsListStateInterface, RootStateInterface> = {
  async fetchClientsByMortgageCached(
    { state: st, commit },
    payload: {
      request: ClientsSourceRequest;
      actingBrokerId?: number | null;
      forceRefresh?: boolean;
    },
  ): Promise<ClientsSourceResponse> {
    const requestKey = JSON.stringify({
      request: payload.request,
      actingBrokerId: payload.actingBrokerId ?? null,
    });
    if (!payload.forceRefresh && st.clientsByMortgageCache[requestKey]) {
      return st.clientsByMortgageCache[requestKey];
    }
    const existingInFlight = clientsByMortgageInFlight[requestKey];
    if (existingInFlight) {
      return existingInFlight;
    }
    const run = async () => {
      const api = new ClientsListApi();
      const data = await api.fetch(payload.request, payload.actingBrokerId);
      commit('setClientsByMortgageCache', { requestKey, data });
      return data;
    };

    const inFlight = run().finally(() => {
      delete clientsByMortgageInFlight[requestKey];
    });
    clientsByMortgageInFlight[requestKey] = inFlight;
    return inFlight;
  },
};

const namespaced: boolean = true;

export const clientsList: Module<ClientsListStateInterface, RootStateInterface> = {
    namespaced,
    state,
    mutations,
    actions,
};